import { Flex } from '@aloft/toolkit-sui';

import { useEffect, useRef } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';

import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { submitObjectiveCharacteristicsForm } from '../../../../redux/features/actions/submitObjectiveCharacteristicsForm.ts';
import { selectObjectiveCharacteristicsForForm } from '../../../../redux/features/selectors/selectObjectiveCharacteristicsForForm.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';
import { Typography } from '../../../../summary/components/Typography.tsx';
import {
  ObjectiveCharacteristicsFormInput,
  ObjectiveCharacteristicsFormOutput,
  schObjectiveCharacteristics,
} from '../../schObjectiveCharacteristics.ts';
import { ObjectiveCharacteristics } from './ObjectiveCharacteristics.tsx';
import { INVALID_FORM_WARNING } from './constants.ts';

import '../../AnalysisModule.scss';

type ObjectiveEditModalProps = {
  activeId: string;
  handleFormValidChange: (isValid: boolean) => void;
};

export const ObjectiveEditForm = ({ activeId, handleFormValidChange }: ObjectiveEditModalProps) => {
  const dispatch = useAppDispatch();
  const defaultFormValues = useAppSelector((state: RootState) =>
    selectObjectiveCharacteristicsForForm(state, activeId),
  );

  const zodForm = useZodForm<typeof schObjectiveCharacteristics>({
    defaultValues: defaultFormValues,
    schema: schObjectiveCharacteristics,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { isValid, isDirty, isSubmitting },
  } = zodForm;

  // Keeping a ref to track this so that we can get the current value in the
  // effect below without running that effect every time isDirty changes (can
  // get rid of this when useEffectEvent is available).
  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    reset(defaultFormValues);
    trigger();

    return () => {
      const onSubmit: SubmitHandler<ObjectiveCharacteristicsFormOutput> = (data) => {
        const { status, contractDate, saleDate, withdrawalDate, expirationDate, ...rest } = data;
        const txDateFields = filterTransactionDateFields({
          status,
          contractDate,
          saleDate,
          expirationDate,
          withdrawalDate,
        });

        dispatch(submitObjectiveCharacteristicsForm({ ...rest, ...txDateFields }));
      };

      const onError: SubmitErrorHandler<ObjectiveCharacteristicsFormInput> = (errors) => {
        console.error(errors);
      };

      if (dirtyRef.current) handleSubmit(onSubmit, onError)();
    };
  }, [activeId]);

  useEffect(() => {
    handleFormValidChange(isValid);
  }, [handleFormValidChange, isValid]);

  return (
    <FormProvider {...zodForm}>
      {!isSubmitting && <ObjectiveCharacteristics id={activeId} />}
      {!isValid && (
        <Flex padding="0.5rem" maxWidth="25rem" flexDirection="column" gap="1rem">
          <Typography variant="body2" color="alertError">
            {INVALID_FORM_WARNING}
          </Typography>
        </Flex>
      )}
    </FormProvider>
  );
};

const filterTransactionDateFields = (
  payload: Pick<
    ObjectiveCharacteristicsFormOutput,
    'status' | 'contractDate' | 'saleDate' | 'withdrawalDate' | 'expirationDate'
  >,
) => {
  const { status, contractDate, saleDate, expirationDate, withdrawalDate } = payload;

  switch (status) {
    case 'Contract':
      return { status, contractDate };
    case 'SettledSale':
      return { status, saleDate, contractDate };
    case 'Withdrawn':
      return { status, withdrawalDate };
    case 'Expired':
      return { status, expirationDate };
    default:
      return { status };
  }
};
